import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { eigeneFormatvorlagen, readFormats, zellenMitIrgendeinerVorlage } from "@/office/excel/formate";
import { nichtPruefbar } from "../schnittstelle";

export const lektionZellformatvorlagen: Lesson = {
  id: "zellformatvorlagen",
  title: "Zellformatvorlagen statt Handarbeit",
  summary: "Ein Format, das einen Namen hat, lässt sich an einer Stelle für die ganze Datei ändern.",
  durationMinutes: 11,
  learningGoals: [
    "Zellformatvorlagen zuweisen statt direkt zu formatieren",
    "Eine eigene Zellformatvorlage anlegen",
    "Erklären, warum benannte Formate bei Änderungen gewinnen",
  ],
  steps: [
    {
      id: "warum-vorlagen",
      title: "Dasselbe Argument wie im Word-Kurs",
      explanation: [
        "Wer eine Kopfzeile fett und blau macht, hat sie formatiert. Wer ihr die " +
          "Zellformatvorlage „Überschrift 1“ zuweist, hat ihr eine Rolle gegeben. Der " +
          "Unterschied ist derselbe wie bei den Formatvorlagen in Word – und er zeigt " +
          "sich erst beim ersten Änderungswunsch.",
        "Soll das Blau der Überschriften künftig grün sein, gibt es zwei Wege. Bei " +
          "direkter Formatierung: jede Überschrift einzeln suchen und ändern, in jedem " +
          "Blatt, und die eine übersehen, die auf Blatt 4 steht. Bei Vorlagen: die " +
          "Vorlage ändern, fertig – alle Zellen, die sie tragen, ziehen mit.",
        "Excel bringt einen Katalog fertiger Zellformatvorlagen mit, zu finden unter " +
          "„Start“ → „Zellformatvorlagen“. Interessant sind darin vor allem die " +
          "Gruppen „Daten und Modell“ und „Titel und Überschriften“: „Eingabe“ für " +
          "Zellen, in die jemand etwas eintragen soll, „Berechnung“ für abgeleitete " +
          "Werte, „Verknüpfte Zelle“ für Verweise auf andere Blätter.",
        "Diese drei zusammen ergeben eine Konvention, die sich in jeder gut gepflegten " +
          "Excel-Datei findet: An der Farbe erkennt man, wo man etwas eintragen darf " +
          "und wo eine Formel steht, die man nicht überschreiben sollte. Das ist der " +
          "eigentliche Zweck – nicht Schönheit, sondern eine Bedienungsanleitung, die " +
          "in der Datei selbst steckt.",
      ],
      keyPoints: [
        "Start → Zellformatvorlagen öffnet den Katalog.",
        "„Eingabe“, „Berechnung“, „Verknüpfte Zelle“ sind eine eingebaute Konvention.",
        "Eine Vorlage ändern wirkt überall; direkte Formatierung muss man suchen gehen.",
      ],
      task: {
        instruction:
          "Weise in deiner Arbeitsmappe mindestens einer Zelle eine Zellformatvorlage " +
          "aus dem Katalog zu – etwa „Eingabe“ für eine Parameterzelle oder " +
          "„Überschrift 1“ für einen Titel.",
        hint: "Zelle markieren, dann Start → Zellformatvorlagen → eine Vorlage anklicken.",
        check: async () => {
          const formate = await readFormats();
          if (formate.cellFormats === null) {
            return nichtPruefbar(
              "Die Zellformatvorlage einer Zelle",
              "1.9",
              "Klicke die Zelle an und sieh unter „Start“ → „Zellformatvorlagen“ nach, welche Vorlage dort hervorgehoben ist.",
            );
          }

          const zugewiesen = zellenMitIrgendeinerVorlage(formate);
          if (zugewiesen.length === 0) {
            return fail(
              "Keine Zelle trägt bisher eine Zellformatvorlage – alle stehen auf „Standard“.",
              "Markiere eine Zelle und wähle unter „Start“ → „Zellformatvorlagen“ eine Vorlage aus. " +
                "Für eine Parameterzelle passt „Eingabe“, für einen Titel „Überschrift 1“.",
            );
          }

          const namen = [...new Set(zugewiesen.map((zelle) => zelle.style ?? ""))];
          return pass(
            `${zugewiesen.length} ${zugewiesen.length === 1 ? "Zelle trägt" : "Zellen tragen"} eine ` +
              `Zellformatvorlage: ${namen.map((name) => `„${name}“`).join(", ")}.`,
          );
        },
      },
    },
    {
      id: "eigene-vorlage",
      title: "Eine eigene Zellformatvorlage anlegen",
      explanation: [
        "Reichen die mitgelieferten Vorlagen nicht, legst du eigene an. Der " +
          "praktischste Weg führt über eine Zelle, die schon so aussieht, wie du es " +
          "willst: markieren, dann „Start“ → „Zellformatvorlagen“ → „Neue " +
          "Zellenformatvorlage“. Excel übernimmt die Formatierung der markierten Zelle " +
          "und fragt nur noch nach einem Namen.",
        "Im Dialog stehen sechs Häkchen: Zahlenformat, Ausrichtung, Schriftart, " +
          "Rahmen, Ausfüllen, Schutz. Nur die angehakten Eigenschaften werden Teil der " +
          "Vorlage – der Rest bleibt so, wie er in der Zelle gerade ist. Das ist " +
          "nützlicher, als es klingt: Eine Vorlage, die nur das Zahlenformat setzt, " +
          "lässt sich mit jeder Farbgebung kombinieren.",
        "Eigene Vorlagen gehören zur Arbeitsmappe, nicht zu Excel. Sie wandern also " +
          "mit der Datei mit, stehen aber in einer neuen Mappe nicht zur Verfügung. Wer " +
          "sie überall haben will, legt sie in einer Vorlagendatei an – das ist Thema " +
          "des letzten Moduls.",
      ],
      keyPoints: [
        "Zelle passend formatieren, dann „Neue Zellenformatvorlage“ – Excel übernimmt alles.",
        "Die sechs Häkchen entscheiden, welche Eigenschaften Teil der Vorlage sind.",
        "Eigene Vorlagen gehören zur Datei, nicht zur Excel-Installation.",
      ],
      task: {
        instruction:
          "Lege eine eigene Zellformatvorlage an und gib ihr einen Namen, der ihren " +
          "Zweck beschreibt.",
        hint: "Eine passend formatierte Zelle markieren, dann Start → Zellformatvorlagen → Neue Zellenformatvorlage.",
        check: async () => {
          const formate = await readFormats();
          if (formate.styles === null) {
            return nichtPruefbar(
              "Die Zellformatvorlagen der Arbeitsmappe",
              "1.7",
              "Öffne den Katalog unter „Start“ → „Zellformatvorlagen“ – eigene Vorlagen stehen ganz oben unter „Benutzerdefiniert“.",
              true,
            );
          }

          const eigene = eigeneFormatvorlagen(formate);
          if (eigene.length === 0) {
            return fail(
              `Die Arbeitsmappe enthält ${formate.styles.length} Zellformatvorlagen, aber alle davon ` +
                "sind von Excel mitgeliefert.",
              "Formatiere eine Zelle so, wie du es brauchst, markiere sie und wähle " +
                "„Start“ → „Zellformatvorlagen“ → „Neue Zellenformatvorlage“.",
            );
          }

          return pass(
            `Eigene Vorlage${eigene.length > 1 ? "n" : ""} gefunden: ` +
              `${eigene.map((vorlage) => `„${vorlage.name}“`).join(", ")}. ` +
              "Eine Änderung daran wirkt sofort auf alle Zellen, die sie tragen.",
          );
        },
      },
    },
  ],
};
